import { EventType } from "@/analytics/analytics-events";
import { SaveExternalAnnotationEventProperties } from "@/analytics/annotation-events-properties";
import { AuthorizeExternalAnnotationProperties } from "@/analytics/generic-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { Tag } from "@/store/tags/tags-slice";
import { grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  ApiResponseError,
  BcfServicesIntegrationType,
  isBcfBackendApiErrorDetails,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useState, type JSX } from "react";
import { AccAnnotationForm } from "./acc-annotation-form";
import { AnnotationTypeDropdownProps } from "./annotation-editor/annotation-editor";
import { AnnotationEditorButtons } from "./annotation-editor/annotation-editor-buttons";
import { AnnotationEditorTypeSelection } from "./annotation-editor/annotation-editor-type-selection";
import { CreateAnnotationFormProps } from "./create-annotation-form";
import { ExternalAnnotationError } from "./external-annotation-error";
import {
  getExternalAnnotationProviderName,
  isDocumentApiImplemented,
} from "./external-annotation-utils";
import { useAnnotationScreenshot } from "./use-annotation-screenshot";

type AccAnnotationProps = CreateAnnotationFormProps &
  AnnotationTypeDropdownProps & {
    /** The BCF Service integration type that corresponds to the ACC annotation to create */
    integrationType:
      | BcfServicesIntegrationType.autodeskAccIssues
      | BcfServicesIntegrationType.autodeskAccRfis;
  };

/**
 * @returns ACC annotation creation form
 */
export function AccAnnotation({
  onClose,
  onSave,
  integrationType: externalAnnotationType,
  selectedAnnotationType,
  onAnnotationTypeChange,
  annotationTypeOptions,
}: AccAnnotationProps): JSX.Element {
  const { handleErrorWithToast } = useErrorHandlers();

  // before first press of the confirm button we don't force validation of all fields
  const [validationRequested, setValidationRequested] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [areMandatoryFieldsFilled, setAreMandatoryFieldsFilled] =
    useState(false);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [description, setDescription] = useState<string>("");
  const [rootCause, setRootCause] = useState<string>("");
  const [topicType, setTopicType] = useState<string>("");
  const [priority, setPriority] = useState<string>("");
  const [selectedWatchers, setSelectedWatchers] = useState<Tag[]>([]);
  const [published, setPublished] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    // Reset the fields when the annotation type changes
    setTitle("");
    setAssignee("");
    setStatus("");
    setDueDate(undefined);
    setStartDate(undefined);
    setDescription("");
    setRootCause("");
    setPriority("");
    setTopicType("");
    setSelectedWatchers([]);
    setAreMandatoryFieldsFilled(false);
    setValidationRequested(false);
  }, [externalAnnotationType]);

  const takeScreenshot = useAnnotationScreenshot();

  const createAnnotation = useCallback(async () => {
    Analytics.track<SaveExternalAnnotationEventProperties>(
      EventType.saveExternalAnnotation,
      {
        type:
          externalAnnotationType === BcfServicesIntegrationType.autodeskAccRfis
            ? "ACC RFI"
            : "ACC Issue",
      },
    );
    setIsSaving(true);
    try {
      await onSave({
        title,
        assignee,
        description,
        dueDate,
        status,
        externalAnnotationType,
        accCompanionFields: {
          selectedWatchers,
          rootCause,
          startDate,
          published,
        },
        topicType,
        priority,
        screenshotFile: isDocumentApiImplemented(externalAnnotationType)
          ? await takeScreenshot()
          : undefined,
      });
    } catch (error) {
      let errorMessage = error;
      if (error instanceof ApiResponseError) {
        if (isBcfBackendApiErrorDetails(error.body)) {
          errorMessage = (
            <ExternalAnnotationError
              integrationType={externalAnnotationType}
              errorDetails={error.body}
              onAuthorizationButtonClicked={() =>
                Analytics.track<AuthorizeExternalAnnotationProperties>(
                  EventType.authorizeExternalAnnotation,
                  {
                    via: "error toast message",
                    service: getExternalAnnotationProviderName(
                      externalAnnotationType,
                    ),
                  },
                )
              }
            />
          );
        }
      }
      // Show the unexpected error messages in a toast
      handleErrorWithToast({
        title: "Unable to create annotation",
        error: errorMessage,
      });
    }
    setIsSaving(false);
  }, [
    onSave,
    title,
    assignee,
    description,
    dueDate,
    status,
    externalAnnotationType,
    selectedWatchers,
    rootCause,
    startDate,
    topicType,
    priority,
    published,
    handleErrorWithToast,
    takeScreenshot,
  ]);

  const disableControls = useCallback(() => {
    setDisabled(true);
  }, [setDisabled]);

  const enableControls = useCallback(() => {
    setDisabled(false);
  }, [setDisabled]);

  return (
    <Stack
      direction="column"
      sx={{
        width: "522px",
        backgroundColor: grey[1000],
        p: 2,
        borderRadius: "4px",
        pointerEvents: isSaving ? "none" : undefined,
        flexGrow: 1,
        gap: 3,
        // Allow the main content to scroll if there's not enough space
        overflowY: "auto",
        // but do not add an horizontal scrollbar when the main content needs to scroll
        overflowX: "clip",
      }}
    >
      <AnnotationEditorTypeSelection
        value={selectedAnnotationType}
        options={annotationTypeOptions}
        onChange={(e) => onAnnotationTypeChange(e.target.value)}
        disabled={isSaving}
      />
      <AccAnnotationForm
        title={title}
        allowEdition={!isSaving}
        assignee={assignee}
        startDate={startDate}
        dueDate={dueDate}
        status={status}
        topicType={topicType}
        priority={priority}
        rootCause={rootCause}
        selectedWatchers={selectedWatchers}
        published={published}
        externalAnnotationType={externalAnnotationType}
        disabled={disabled}
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        onAssigneeChange={setAssignee}
        onStartDateChange={setStartDate}
        onDueDateChange={setDueDate}
        onStatusChange={setStatus}
        onRootCauseChange={setRootCause}
        onTopicTypeChange={setTopicType}
        onPriorityChange={setPriority}
        onSelectedWatchersChange={setSelectedWatchers}
        onPublishedChange={setPublished}
        showMandatoryFieldsError={validationRequested}
        setAreMandatoryFieldsFilled={setAreMandatoryFieldsFilled}
        onConnectionTokenExpired={disableControls}
        onConnectionTokenUpdated={enableControls}
      />
      <AnnotationEditorButtons
        disabledConfirmButton={
          (validationRequested && !areMandatoryFieldsFilled) || disabled
        }
        disabled={isSaving}
        showConfirmButtonSpinner={isSaving}
        confirmButtonText={isSaving ? "Creating" : "Create"}
        onCancelButtonClick={onClose}
        onConfirmButtonClick={() => {
          if (areMandatoryFieldsFilled) {
            createAnnotation();
          } else {
            setValidationRequested(true);
          }
        }}
      />
    </Stack>
  );
}
